import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// Pasted declarations that paint the text. Everything else — weight, style,
// decoration, alignment — is formatting, and stays for the schema to parse.
const COLOR_STYLES = new Set(["color", "background", "background-color"]);

// Color attributes from Word, Outlook and old HTML mail.
const COLOR_ATTRS = ["color", "bgcolor"];

function dropColorStyles(style: string): string {
  return style
    .split(";")
    .filter((decl) => {
      const prop = decl.split(":")[0]?.trim().toLowerCase();
      return prop && !COLOR_STYLES.has(prop);
    })
    .map((decl) => decl.trim())
    .join("; ");
}

/**
 * Clipboard HTML → the same HTML without its colors, so pasted text wears the
 * theme's foreground in light and dark. Also drops the clipboard's head junk
 * (style sheets, meta, comments). Runs before the schema parse, so every
 * style a mark or node reads (Google Docs bold is `font-weight:700`) arrives.
 */
export function cleanPastedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const el of Array.from(doc.querySelectorAll("style, meta, link, script, title"))) {
    el.remove();
  }
  const comments = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
  const dead: Node[] = [];
  while (comments.nextNode()) dead.push(comments.currentNode);
  for (const node of dead) node.parentNode?.removeChild(node);
  for (const el of Array.from(doc.body.querySelectorAll("*"))) {
    for (const attr of COLOR_ATTRS) el.removeAttribute(attr);
    const style = el.getAttribute("style");
    if (style === null) continue;
    const kept = dropColorStyles(style);
    if (kept) el.setAttribute("style", kept);
    else el.removeAttribute("style");
  }
  return doc.body.innerHTML;
}

/**
 * The paste rule every Tiptap editor shares: pasted formatting stays, pasted
 * color goes. Add it to each editor's extensions.
 */
export const PasteHygiene = Extension.create({
  name: "pasteHygiene",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("pasteHygiene"),
        props: { transformPastedHTML: cleanPastedHtml },
      }),
    ];
  },
});
